import type {
  GetMbcBriefResponse,
  MbcBrief,
  MbcBriefQuality,
  SubmitMbcBriefResponse,
  UpdateMbcBriefBody,
  UpdateMbcBriefResponse,
} from "@nlc/shared-types";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { requestIdFromSessionId } from "@/features/request/mbc-chat/lib/session-ids";
import { requestKeys, requestListKeys } from "@/hooks/queryKeys";

import { MBC_AI_API_URL } from "./config";

type UseReviewBriefArgs = {
  resourceId?: string;
  threadId?: string;
};

type UseReviewBriefResult = {
  brief: MbcBrief | null;
  error: Error | null;
  isLoading: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  patchBrief: (patch: UpdateMbcBriefBody) => Promise<MbcBrief>;
  quality: MbcBriefQuality | null;
  refetchBrief: () => Promise<void>;
  submitBrief: () => Promise<SubmitMbcBriefResponse>;
};

type BriefQueryData = GetMbcBriefResponse;

const PENDING_REFETCH_MS = 5000;

const reviewBriefKey = (threadId?: string, resourceId?: string) =>
  ["mbc-review-brief", threadId, resourceId] as const;

function briefUrl(threadId: string, resourceId?: string, suffix = ""): string {
  const params = new URLSearchParams();

  if (resourceId) params.set("resourceId", resourceId);

  const query = params.toString();
  const base = `${MBC_AI_API_URL}/mbc/brief/${encodeURIComponent(threadId)}${suffix}`;

  return query ? `${base}?${query}` : base;
}

/**
 * Reads and persists the MBC brief (the agent's working memory) straight from
 * the AI module — no backend hop. The brief is the shared source of truth: the
 * chat agent fills it in, and the review page edits the connected fields back.
 */
export function useReviewBrief({
  resourceId,
  threadId,
}: UseReviewBriefArgs): UseReviewBriefResult {
  const queryClient = useQueryClient();
  const queryKey = reviewBriefKey(threadId, resourceId);
  const requestId = threadId ? requestIdFromSessionId(threadId) : null;

  const query = useQuery({
    enabled: Boolean(threadId),
    queryFn: async (): Promise<BriefQueryData> => {
      const response = await fetch(briefUrl(threadId!, resourceId));

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      return (await response.json()) as GetMbcBriefResponse;
    },
    queryKey,
    refetchInterval: (current) =>
      current.state.data?.quality.items.some(
        (item) => item.status === "pending",
      )
        ? PENDING_REFETCH_MS
        : false,
  });

  const mutation = useMutation({
    mutationFn: async (
      patch: UpdateMbcBriefBody,
    ): Promise<UpdateMbcBriefResponse> => {
      const response = await fetch(briefUrl(threadId!, resourceId), {
        body: JSON.stringify(patch),
        headers: { "Content-Type": "application/json" },
        method: "PATCH",
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      return (await response.json()) as UpdateMbcBriefResponse;
    },
    // The PATCH re-judges the edit inline, so its response already carries the
    // merged brief and the recomputed quality — no refetch needed.
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKey,
        (previous: BriefQueryData | undefined) => ({
          ...previous,
          ...data,
        }),
      );
    },
  });

  // Submit hands the brief to the AI module, which forwards its plain text to
  // the backend — the only writer of the request row's brief fields and its
  // attachments. Nothing is sent in the body: the working memory the
  // server already holds is the authoritative brief.
  const submitMutation = useMutation({
    mutationFn: async (): Promise<SubmitMbcBriefResponse> => {
      const response = await fetch(briefUrl(threadId!, resourceId, "/submit"), {
        method: "POST",
      });

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;

        throw new Error(body?.error ?? "Please try again.");
      }

      return (await response.json()) as SubmitMbcBriefResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      if (requestId != null) {
        void queryClient.invalidateQueries({
          queryKey: requestKeys.requestById(requestId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: requestListKeys.all });
    },
  });

  return {
    brief: query.data?.brief ?? null,
    error: query.error,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    isSubmitting: submitMutation.isPending,
    patchBrief: async (patch) => {
      const data = await mutation.mutateAsync(patch);

      return data.brief;
    },
    quality: query.data?.quality ?? null,
    refetchBrief: () => queryClient.invalidateQueries({ queryKey }),
    submitBrief: submitMutation.mutateAsync,
  };
}
